import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, LayoutTemplate, Star, TrendingUp, ArrowRight,
  Sparkles, Users, Shield,
} from 'lucide-react';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import usePageMeta from '../../lib/usePageMeta';

const Stat = ({ value, label }) => (
  <div className="text-center">
    <p className="text-3xl font-bold text-white">{value}</p>
    <p className="text-sm text-white/60 mt-1">{label}</p>
  </div>
);

const Feature = ({ icon: Icon, title, body }) => (
  <div className="flex gap-4">
    <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-violet-600" />
    </div>
    <div>
      <p className="font-semibold text-gray-900">{title}</p>
      <p className="text-sm text-gray-500 mt-0.5">{body}</p>
    </div>
  </div>
);

const Step = ({ n, title, body }) => (
  <div className="flex gap-5 items-start">
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
      {n}
    </div>
    <div>
      <p className="font-semibold text-gray-900">{title}</p>
      <p className="text-sm text-gray-500 mt-0.5">{body}</p>
    </div>
  </div>
);

const CreatorBecome = () => {
  const navigate = useNavigate();
  usePageMeta('Become a creator', 'Publish your templates on the iVisora marketplace.', { path: '/creator/become' });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0f1117] py-24 px-6 text-center">
        <div
          aria-hidden
          className="absolute inset-0 opacity-40"
          style={{ background: 'radial-gradient(ellipse 80% 60% at 50% -20%,rgba(124,58,237,0.6),transparent 70%)' }}
        />
        <div className="relative max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 bg-violet-500/20 text-violet-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
            <Sparkles className="w-3 h-3" /> Creator Marketplace
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight">
            Turn your designs into<br />
            <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              passive income
            </span>
          </h1>
          <p className="mt-5 text-lg text-white/60 max-w-xl mx-auto">
            Publish your templates on the IVisora Marketplace and earn every time someone downloads your work.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => navigate('/creator/onboarding/profile')}
              className="flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-semibold hover:opacity-90 transition-opacity shadow-xl shadow-violet-900/30"
            >
              Start selling <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/marketplace')}
              className="flex items-center gap-2 px-7 py-3 rounded-full bg-white/10 text-white font-semibold hover:bg-white/15 transition-colors"
            >
              Browse marketplace
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative max-w-2xl mx-auto mt-16 grid grid-cols-3 gap-8 border-t border-white/10 pt-10">
          <Stat value="70%" label="Creator revenue share" />
          <Stat value="Free" label="To list your templates" />
          <Stat value="Instant" label="Access to all buyers" />
        </div>
      </section>

      {/* Features */}
      <section className="max-w-4xl mx-auto py-20 px-6">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">Everything you need to succeed</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <Feature
            icon={DollarSign}
            title="Earn 70% of every sale"
            body="Set your own prices. Keep 70% of every purchase. Payouts processed on request."
          />
          <Feature
            icon={LayoutTemplate}
            title="Use the full IVisora editor"
            body="Create templates using the same powerful editor your customers will use."
          />
          <Feature
            icon={Star}
            title="Build your reputation"
            body="Collect reviews, respond to feedback, and build a public profile buyers trust."
          />
          <Feature
            icon={TrendingUp}
            title="Analytics & insights"
            body="Track views, sales, conversion rates, and revenue per template in real time."
          />
          <Feature
            icon={Users}
            title="Grow a following"
            body="Buyers can follow your store. Build an audience that gets notified on new releases."
          />
          <Feature
            icon={Shield}
            title="Template approval review"
            body="Every template is reviewed for quality before going live — keeping buyers happy and your brand strong."
          />
        </div>
      </section>

      {/* How it works */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">How it works</h2>
          <div className="space-y-8">
            <Step n="1" title="Set up your creator profile" body="Tell buyers who you are. Add a bio, your social links, and your display name." />
            <Step n="2" title="Create your first template" body="Design in the IVisora editor, save as a draft, and submit it for review." />
            <Step n="3" title="Get approved & go live" body="Our team reviews your template — usually within 24 hours. Once approved it appears on the marketplace." />
            <Step n="4" title="Earn on every sale" body="When someone buys your template, 70% lands in your creator earnings. Request a payout anytime." />
          </div>
          <div className="mt-12 text-center">
            <button
              onClick={() => navigate('/creator/onboarding/profile')}
              className="flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-violet-500/25 mx-auto"
            >
              Get started for free <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CreatorBecome;
